import {
  useExamplesForEndpoint,
  useGraphApi,
  useGraphEndpoint,
} from '@squareup/dex-feature-hooks-graph';
import { SpecVersionPickerContainer } from '@squareup/dex-feature-shared-main-specification-version-picker';
import { useCodeLanguage } from '@squareup/dex-store-access-shared-main-selectors';
import {
  PropertyCategory,
  ReferenceErrorContainer,
  ReferencePropertyTable,
} from '@squareup/dex-tech-reference-properties';
import {
  ReferenceInfoBox,
  ReferenceCodeExampleAsideContainer,
} from '@squareup/dex-tech-ui';
import {
  parseExamplesPathParam,
  ReferenceHrefBuilders,
  toUrl,
} from '@squareup/dex-tech-utils-routing';
import { ReferenceExampleScenario } from '@squareup/dex-types-oas';
import {
  HeadWrapper,
  HttpMethodName,
  HttpMethods,
  Badge,
  BadgeType,
  Eyebrow,
} from '@squareup/dex-ui';
import { Markdown } from '@squareup/dex-ui-markdown';
import { Display20, Box } from '@squareup/dex-ui-shared-base';
import { ContentLayoutGrid } from '@squareup/dex-ui-shared-content-layout-grid';
import { useDecision } from '@squareup/dex-ui-shared-feature-detection';
import {
  getNextPublicApiReferenceNameOrDefault,
  getNextPublicSquareDocsURL,
} from '@squareup/dex-utils-environment';
import { toApiId } from '@squareup/dex-utils-oas';
import { useParams } from '@squareup/dex-utils-shared-routing-hooks';
import { humanizeName } from '@squareup/dex-utils-text';
import React, { ReactNode, useMemo } from 'react';

import { SquareVersionPage } from '../../../../shared/SquareVersionPage';

import styles from './[endpoint].module.css';

const PageContent = () => {
  const query = useParams();
  const square_version = query?.square_version as string;

  const { decision } = useDecision('narrow_property_table');
  const narrowViewEnabled = decision.enabled;

  const { endpoint } = useGraphEndpoint();
  const { api } = useGraphApi();

  const [codeLanguage, setCodeLanguage] = useCodeLanguage();
  const examples = useExamplesForEndpoint(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    parseExamplesPathParam(square_version)!,
    codeLanguage
  );

  const exampleScenarios = useMemo(() => {
    if (examples.error || !examples.data) {
      return [];
    }

    return examples.data as ReferenceExampleScenario[];
  }, [examples]);

  const backHref = useMemo(() => {
    if (endpoint?.isDeprecated) {
      return ReferenceHrefBuilders.versionDeprecated({
        square_version,
      });
    } else {
      return ReferenceHrefBuilders.versionApi({
        square_version,
        api: toApiId(api?.id),
      });
    }
  }, [square_version, api, endpoint]);

  const deprecationDate = endpoint?.deprecationInfo?.deprecationDate;
  const retirementDate = endpoint?.deprecationInfo?.retirementDate;
  const replacementEndpoint = endpoint?.deprecationInfo?.replacedBy;
  const migrationGuideUrl = endpoint?.deprecationInfo?.guideUrl;

  if (!endpoint) {
    return null;
  }

  const pathParameters = endpoint.pathParameters;
  const queryParameters = endpoint.queryParameters;
  const requestBodyProperties = endpoint?.requestBody?.properties;
  const responseFields = endpoint?.responseBody?.properties;
  const sortedEndpointErrors = getSortedEndpointErrors(endpoint);
  const httpMethod = endpoint?.isDeprecated
    ? 'DEPRECATED'
    : (endpoint.httpMethod.toUpperCase() as HttpMethods);
  const description = endpoint.summary ?? '';
  const title = `${httpMethod} ${
    endpoint.path
  } - ${getNextPublicApiReferenceNameOrDefault()}`;

  const isBeta = endpoint.releaseStatus === 'BETA';
  const betaBadge = isBeta ? (
    <Badge className={styles['beta-badge']} type={BadgeType.Beta} />
  ) : null;

  const versionSwitcher = <SpecVersionPickerContainer variant="reference" />;

  return (
    <>
      <HeadWrapper
        description={description}
        preconnectUrl={getNextPublicSquareDocsURL()}
        title={title}
        canonicalPath={
          api
            ? toUrl(
                ReferenceHrefBuilders.versionApiEndpoint({
                  square_version,
                  api: `${api.id}-api`,
                  endpoint: endpoint.id,
                })
              )
            : undefined
        }
      />
      <ContentLayoutGrid>
        <section>
          <Box className={styles.eyebrow} margin={{ bottom: '1x' }}>
            <Eyebrow backHref={backHref}>{api?.name} API</Eyebrow>
            <Box className={styles['version-switcher']}>{versionSwitcher}</Box>
          </Box>
          <Display20 data-testid="endpoint-name-heading">
            {humanizeName(endpoint.name, { style: 'sentence' })}
            {betaBadge}
          </Display20>
          <HttpMethodName httpMethod={httpMethod} name={endpoint.path} />
          {endpoint.summary && (
            <Markdown
              markdown={endpoint.summary}
              testId="endpoint-summary"
            ></Markdown>
          )}
          {endpoint.description && (
            <Markdown
              markdown={endpoint.description}
              testId="endpoint-description"
            ></Markdown>
          )}
          <Box className="home-header__inner">
            <Box className="home-header__innertext"></Box>
            <ReferenceInfoBox
              api={api}
              endpoint={endpoint}
              versionInfo={square_version}
              permissions={endpoint.scopes}
              externalDocs={endpoint.externalDocs}
              deprecationDate={deprecationDate}
              retirementDate={retirementDate}
              replacementEndpoint={replacementEndpoint}
              migrationGuideUrl={migrationGuideUrl}
              explorerButtonModel={!(endpoint.isDeprecated && endpoint)}
            ></ReferenceInfoBox>
          </Box>

          {endpoint.hasPathParameters && (
            <ReferencePropertyTable
              properties={pathParameters}
              propertyCategory={PropertyCategory.PathParameters}
              showExampleCode={true}
              narrowView={narrowViewEnabled}
            />
          )}
          {endpoint.hasQueryParameters && (
            <ReferencePropertyTable
              properties={queryParameters}
              propertyCategory={PropertyCategory.QueryParameters}
              showExampleCode={!endpoint.hasPathParameters}
              narrowView={narrowViewEnabled}
            />
          )}
          {endpoint.hasRequestBody && (
            <ReferencePropertyTable
              properties={requestBodyProperties}
              propertyCategory={PropertyCategory.RequestBody}
              showExampleCode={
                !(endpoint.hasPathParameters || endpoint.hasQueryParameters)
              }
              narrowView={narrowViewEnabled}
            />
          )}
          <ReferencePropertyTable
            properties={responseFields}
            propertyCategory={PropertyCategory.ResponseFields}
            narrowView={narrowViewEnabled}
          />
        </section>
        <aside>
          <ReferenceCodeExampleAsideContainer
            versionSwitcher={versionSwitcher}
            codeLanguage={codeLanguage}
            setCodeLanguage={setCodeLanguage}
            scenarios={exampleScenarios}
            endpoint={endpoint}
          />
        </aside>
        {endpoint.hasErrors && (
          <ReferenceErrorContainer
            errors={sortedEndpointErrors}
            tableName="Error descriptions"
            // @ts-ignore we have used hasErrors in conditon
            selectedErrorRow={sortedEndpointErrors[0]}
          ></ReferenceErrorContainer>
        )}
      </ContentLayoutGrid>
    </>
  );
};

class Page extends SquareVersionPage {
  public static override routeHandlerName = '[square_version]/[api]/[endpoint]';

  protected override getPageContent(): ReactNode {
    return super.getPageContent({
      children: <PageContent />,
    });
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSortedEndpointErrors(endpoint: any) {
  if (!endpoint.errors) {
    return [];
  }

  return [...endpoint.errors].sort((a, b) => {
    if (a.httpCode === b.httpCode) {
      return b.name - a.name;
    } else {
      return a.httpCode - b.httpCode;
    }
  });
}

export default Page;
